<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Ein 360°-Foto ist ein sphärisches Bild, das gleichzeitig eine
            vollständige 360°-Horizontalansicht und eine 180°-Vertikalansicht
            erfasst – und damit eine vollständige fotografische Aufzeichnung
            jeder Richtung von einem einzigen Punkt im Raum erstellt. Anders als
            eine Standardfotografie, die einen rechteckigen Ausschnitt Ihres
            Sichtfelds erfasst, erfasst ein 360°-Foto die gesamte Umgebung um
            den Fotografen herum. Wenn Sie ein 360°-Foto betrachten, können Sie
            schwenken, zoomen und in jede Richtung schauen, einschließlich
            geradeaus nach oben und geradeaus nach unten.
          </p>
        </section>

        <section>
          <h2>Wie 360°-Fotos erstellt werden</h2>

          <h3>Der technische Prozess</h3>
          <p>
            360°-Fotos werden durch einen Prozess namens
            <strong>Bildkomposition</strong> erstellt. Mehrere überlappende
            Fotografien – aus verschiedenen Richtungen vom selben Punkt
            aufgenommen – werden mathematisch ausgerichtet und gemischt, um eine
            nahtlose Kugel zu erstellen. Die resultierende Datei wird
            typischerweise als äquirektanguläres Bild gespeichert: eine flache,
            rechteckige Datei (normalerweise mit einem 2:1-Seitenverhältnis),
            die die sphärische Oberfläche auf eine flache Ebene abbildet –
            ähnlich wie Weltkarten die kugelförmige Erde auf einer flachen Seite
            darstellen.
          </p>
          <p>
            Dedizierte 360°-Kameras (wie die Insta360, Ricoh Theta oder ähnliche
            Geräte) tun dies automatisch mit dualen Fischauge-Objektiven.
            Smartphone-Apps wie Travvir erzielen das gleiche Ergebnis, indem sie
            Sie anleiten, mehrere Frames in Sequenz aufzunehmen.
          </p>

          <h3>Das äquirektanguläre Format erklärt</h3>
          <p>
            Wenn Sie eine 360°-Fotodatei herunterladen und in einem
            Standard-Bildbetrachter öffnen, sieht sie verzerrt und gestreckt aus
            – besonders oben und unten. Diese Verzerrung ist das natürliche
            Ergebnis der Projektion einer Kugel auf eine flache Oberfläche.
            Spezialisierte 360°-Betrachter (wie die in Travvir, Google Street
            View, Facebook, YouTube und Matterport verwendeten) re-projizieren
            diese flache äquirektanguläre Datei auf eine Kugel, sodass der
            Betrachter natürlich umherschauen kann.
          </p>
        </section>

        <section>
          <h2>Typen von 360°-Inhalten</h2>

          <h3>Statische 360°-Fotos</h3>
          <p>
            Ein einzelnes sphärisches Bild von einem festen Punkt im Raum. Wenn
            Sie es in einem Betrachter ansehen, können Sie Ihre Ansicht in jede
            Richtung drehen, aber Sie können sich nicht durch den Raum bewegen.
            Dies sind die häufigsten 360°-Inhalte und die Bausteine von Virtual
            Tours.
          </p>

          <h3>360°-Virtual-Tours</h3>
          <p>
            Eine Sammlung von 360°-Fotos, die von mehreren Positionen innerhalb
            eines Raums aufgenommen und durch Navigations-Hotspots verbunden
            wurden. Dies ist das, was Travvir und Plattformen wie Matterport
            erstellen.
          </p>

          <h3>360°-Video</h3>
          <p>
            Ähnlich wie 360°-Fotos im Hinblick auf die sphärische
            Erfassungsmethode, aber zeichnet kontinuierliches Video auf.
            360°-Video kann nicht navigiert werden, aber die Ansicht ist
            interaktiv – Sie können in jede Richtung schauen, während das Video
            abgespielt wird. 360°-Videodateien sind extrem groß und werden
            typischerweise gestreamt.
          </p>

          <h3>Photogrammetrie und 3D-Scans (vs. 360°)</h3>
          <p>
            Photogrammetrie-basierte Tools wie Matterport produzieren eine
            andere Art von Ausgabe: ein 3D-Netzmodell eines Raums aus vielen
            überlappenden Fotografien, die von KI verarbeitet wurden. Das
            Ergebnis sieht anders aus – mehr wie eine Videospielumgebung als
            fotografische Bilder.
          </p>
        </section>

        <section>
          <h2>Wie 360°-Fotos angezeigt werden</h2>

          <h3>Browser-basierte Betrachter</h3>
          <p>
            Plattformen wie Travvir, Kuula und Momento360 zeigen 360°-Fotos über
            JavaScript-WebGL-Rendering in einem Standard-Webbrowser an. Dies
            ermöglicht das Einbetten von 360°-Fotos in Websites mit einem
            iFrame-Tag, das Teilen über Links oder das direkte Veröffentlichen
            auf Social-Media-Plattformen.
          </p>

          <h3>Mobile App-Anzeige</h3>
          <p>
            360°-Fotos in mobilen Apps können das Gyroskop des Geräts nutzen, um
            orientierungsgesteuertes Betrachten zu ermöglichen: Neigen und
            Drehen Ihres physischen Handys ändert, was Sie in der 360°-Szene
            sehen.
          </p>

          <h3>VR-Headset-Anzeige</h3>
          <p>
            Die gleiche äquirektanguläre 360°-Fotodatei, die im Browser
            angezeigt wird, kann in einem VR-Headset (Meta Quest, Apple Vision
            Pro, Cardboard usw.) für ein vollständig immersives Erlebnis
            angesehen werden.
          </p>
        </section>

        <section>
          <h2>Häufige Fragen zu 360°-Fotos</h2>

          <h3>
            Kann ich ein 360°-Foto mit meinem normalen Smartphone aufnehmen?
          </h3>
          <p>
            Ja. Smartphone-Apps einschließlich Travvir, Google Street View und
            andere verwenden die einzelne Kamera Ihres Handys und geführte
            Aufnahme, um 360°-Fotos zu erstellen. Die App fordert Sie auf, sich
            zu drehen und mehrere überlappende Frames aufzunehmen, und fügt sie
            automatisch zusammen. Siehe unseren Leitfaden zu
            <a href="/blog/de/brauche-ich-360-kamera"
              >ob Sie eine dedizierte 360°-Kamera brauchen</a
            >
            für einen detaillierten Vergleich.
          </p>

          <h3>Wie groß ist eine 360°-Fotodatei?</h3>
          <p>
            Ein typisches 360°-Foto, das von einer Smartphone-App erstellt
            wurde, reicht von <strong>8–25 MB</strong> als JPEG-Datei.
            Dedizierte 360°-Kameras produzieren Dateien im Bereich von
            <strong>20–80 MB</strong>
            bei höheren Auflösungen. Nach der Plattformkomprimierung beträgt die
            effektiv gelieferte Dateigröße typischerweise 2–8 MB.
          </p>

          <h3>Wie unterscheiden sich 360°-Fotos von Panoramafotos?</h3>
          <p>
            Ein Panoramafoto ist ein Breitwinkelgeschoss – typischerweise
            breiter als ein Standardfoto, aber immer noch ein flaches Rechteck.
            Ein 360°-Foto ist eine vollständige Kugel – es umhüllt Sie in jede
            Richtung. Ein Panoramafoto ist eine Teilmenge dessen, was Sie von
            einem bestimmten Punkt sehen könnten; ein 360°-Foto ist die
            vollständige Ansicht von allem, was von diesem Punkt aus sichtbar
            ist.
          </p>
        </section>

        <section>
          <h2>Fazit: Das Wesentliche der 360°-Fotografie</h2>
          <p>
            Ein 360°-Foto erfasst die vollständige Umgebung von einem einzigen
            Punkt im Raum, gespeichert als äquirektanguläres Bild, das
            Betrachter interaktiv erkunden können. Die Technologie ist jetzt
            durch jedes moderne Smartphone über Apps wie Travvir zugänglich, und
            der Output kann bei Google, Websites, Social Media und
            VR-Plattformen mit derselben Datei veröffentlicht werden.
          </p>
          <p>
            Für Unternehmen bilden 360°-Fotos die Grundlage von Virtual Tours,
            die das lokale SEO verbessern, die Konversionsraten steigern und
            Kunden helfen, sichere Kaufentscheidungen zu treffen.
          </p>
        </section>
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li><a href="/blog/de/360-fotos-mit-smartphone-aufnehmen">360°-Fotos mit Smartphone : Vollständige Anleitung</a></li>
            <li><a href="/blog/de/360-fotos-mit-handy-aufnehmen">360°-Fotos mit Handy aufnehmen : Anleitung</a></li>
            <li><a href="/blog/de/unscharfe-360-fotos-beheben">Unscharfe 360°-Fotos beheben : Ursachen & Lösungen</a></li>
            <li><a href="/blog/de/360-virtual-tour-bildgroesse">360°-Virtual-Tour-Bildgröße : Optimale Einstellungen</a></li>
            <li><a href="/blog/de/smartphone-panorama-vs-360-virtual-tour">Smartphone-Panorama vs. 360° Virtual Tour</a></li>
            <li><a href="/blog/de/360-foto-soziale-medien-teilen">360°-Foto in sozialen Medien teilen</a></li>
            <li><a href="/blog/de/360-virtual-tour">360° Virtual Tour : Der komplette Leitfaden</a></li>
            <li><a href="/blog/de/brauche-ich-360-kamera">Brauche ich eine 360°-Kamera? Ehrliche Antwort</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "was-ist-ein-360-foto",
);

useBlogSeo({
  title: "Was ist ein 360°-Foto? [2026] Vollständige Erklärung | Travvir",
  description:
    "Was ist ein 360°-Foto, wie entsteht es und wofür wird es genutzt? Vollständige Erklärung zu Technik, Erstellung und Anwendungen.",
  slug: "was-ist-ein-360-foto",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
